import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CarService } from 'src/app/services/car/car.service';
import { VehicleBidsService } from 'src/app/services/bids/vehicle-bids.service';
import { BidInquiryService } from 'src/app/services/bid-inquiry/bid-inquiry.service';
import { BidUnitService } from 'src/app/services/bid-unit/bid-unit.service';
import { UserService } from 'src/app/services/user/user.service';
import { VehicleService } from 'src/app/services/vehicle/vehicle.service';
import { PortService } from 'src/app/services/port/port.service';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { UpdateBidUnitComponent } from 'src/app/layout/modals/update-bid-unit/update-bid-unit.component';
import Swal from 'sweetalert2';
import { GlobalStateService } from 'src/app/services/global/global-state.service';

interface VehicleData {
  _id: string;
  inventory_no: string;
  name: string;
  slug: string;
  company_name: string;
  company_slug: string;
  category_id: string | null;
  year: string;
  mileage: number;
  fuel_type: string;
  auction_date: string;
  lot_no: string;
  fuel_type_slug: string;
  transmission: string;
  transmission_slug: string;
  doors: number | null;
  color: string;
  color_slug: string;
  vin_no: string | null;
  location: string | null;
  price: string;
  one_line_description: string | null;
  engine: string;
  engine_capacity: number;
  inspection_expiry_date: string | null;
  car_length: number | null;
  car_width: number | null;
  car_height: number | null;
  hall_name?: string;
  airbag: boolean | null;
  air_condition: boolean | null;
  sunroof: boolean | null;
  auction_date_only: string;
  leather_seat: boolean | null;
  tv: boolean | null;
  keyless: boolean | null;
  smart_key: boolean | null;
  floormat: boolean | null;
  backcamera: boolean | null;
  pictures_count: number;
  pictures: string[];
  is_sold: boolean;
  type: string;
  parsed_data: {
    [key: string]: string;
  };
  service_data: {
    [key: string]: string;
  };
  created_at: string;
  doc_id: string;
  is_added_to_stock?: boolean;
  vehicle_id?: string;
}

@Component({
  selector: 'app-view-bid',
  templateUrl: './view-bid.component.html',
  styleUrls: ['./view-bid.component.scss']
})
export class ViewBidComponent extends DestructorComponent {

  @ViewChild(UpdateBidUnitComponent) updateBidUnitModal!: UpdateBidUnitComponent;

  // Vehicle data
  vehicleData: VehicleData | null = null;
  isLoading: boolean = true;
  vehicleId: string = '';

  // Image gallery
  selectedImageIndex: number = 0;

  // Bid form
  bidForm: FormGroup;
  isSubmittingBid: boolean = false;

  // Inquiry form
  inquiryForm: FormGroup;
  isSubmittingInquiry: boolean = false;

  // UI state
  showInspectionSheet: boolean = false;
  activeTab: string = 'overview';

  // Bid unit loading state
  isLoadingBidUnit: boolean = false;
  currentBidUnit: any = null;
  showEditIcon: boolean = false;

  // Client search properties
  clientSearchTerm: string = '';
  selectedClient: any = null;
  clientSearchResults: any[] = [];
  showClientDropdown: boolean = false;
  isClientSearching: boolean = false;
  private clientSearchSubject = new Subject<string>();

  // Add to stock loading state
  isAddingToStock: boolean = false;

  // Logged in user
  loggedInUser: any = null;
  isClientFieldDisabled: boolean = false;

  // Country and Port properties
  countries: any[] = [];
  ports: any[] = [];
  isLoadingPorts: boolean = false;

  // C&F Price properties
  cfPrice: number | null = null;
  isLoadingCFPrice: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private _carService: CarService,
    private _vehicleBidsService: VehicleBidsService,
    private _bidInquiryService: BidInquiryService,
    private _bidUnitService: BidUnitService,
    private _userService: UserService,
    private _vehicleService: VehicleService,
    private _portService: PortService,
    private _globalStateService: GlobalStateService
  ) {
    super()
    this.initializeBidForm();
    this.initializeInquiryForm();
    this.setupClientSearchDebounce();
  }

  ngOnInit(): void {
    this.getVehicleIdFromRoute();
    this.loadVehicleData();
    this.checkAndSetLoggedInUserAsClient();
    this.loadCountries();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  checkAndSetLoggedInUserAsClient(): void {
    this.subscriptions.push(
      this._globalStateService.user.subscribe(user => {
        if (user) {
          this.loggedInUser = user;

          // Check if user role_type is 20
          if (user.role_type === 20) {
            // Auto-populate client field with logged-in user
            this.selectedClient = user;
            this.clientSearchTerm = this.getClientDisplayName(user);
            this.bidForm.patchValue({ client: user });
            this.isClientFieldDisabled = true;

            // Auto-populate destination country and port if user has them
            if (user.country_obj && user.country_obj.code) {
              this.bidForm.patchValue({ destinationCountry: user.country_obj.code });
            }
            if (user.port_obj && user.port_obj._id) {
              // Wait a bit for ports to load, then set the port
              setTimeout(() => {
                this.bidForm.patchValue({ destinationPort: user.port_obj._id });
              }, 500);
            }
          } else {
            this.isClientFieldDisabled = false;
          }
        }
      })
    );
  }

  checkUserHasAccess(type: string | null){

    if(!type) return false;
    let hasAccess = false;
    this.subscriptions.push(
      this._globalStateService.user.subscribe(user => {
        if (user?.role_complete.is_admin) {
          hasAccess = true;
        }
        else {
          console.log("user",user?.role_complete.permissions['search-bids']);
          console.log("type",type);
          switch(type) {
            case 'aucnet':
              hasAccess = user?.role_complete?.permissions['aucnet-data-bids']?.view || false;
              break;
            case 'one-price':
              hasAccess = user?.role_complete.permissions['one-price-data-bids']?.view || false;
              break;
            case 'alaedo':
              hasAccess = user?.role_complete.permissions['search-bids']?.view || false;
              break;
          }
        }

        if(!hasAccess){
          this.router.navigate(['/dashboard']);
        }
      })
    );
  }

  private initializeBidForm(): void {
    this.bidForm = this.fb.group({
      bidAmount: ['', [Validators.required, Validators.min(6000)]],
      currency: ['JPY', [Validators.required]],
      bidType: ['order', [Validators.required]],
      numberOfUnits: [{ value: '', disabled: true }],
      group: ['', [Validators.required]],
      message: [''],
      priceType: ['', [Validators.required]],
      priceTypeAmount: ['', [Validators.required, Validators.min(0)]],
      client: [null],
      destinationCountry: [''],
      destinationPort: ['']
    });

    // Listen to group changes
    this.bidForm.get('group')?.valueChanges.subscribe((group) => {
      this.onGroupChange(group);
    });

    // Listen to destination country changes
    this.bidForm.get('destinationCountry')?.valueChanges.subscribe((countryCode) => {
      this.onDestinationCountryChange(countryCode);
    });

    // Listen to destination port changes
    this.bidForm.get('destinationPort')?.valueChanges.subscribe((portId) => {
      this.onDestinationPortChange(portId);
    });

    // Listen to price type changes
    this.bidForm.get('priceType')?.valueChanges.subscribe((priceType) => {
      this.onPriceTypeChange(priceType);
    });
  }

  private initializeInquiryForm(): void {
    this.inquiryForm = this.fb.group({
      inquiryMessage: ['', [Validators.required]]
    });
  }

  // Handle group selection change
  onGroupChange(group: string): void {
    if (!group || !this.vehicleData) {
      // Reset and disable the numberOfUnits field
      this.bidForm.patchValue({ numberOfUnits: '' });
      this.bidForm.get('numberOfUnits')?.disable();
      this.showEditIcon = false;
      this.currentBidUnit = null;
      return;
    }

    // Fetch bid unit data
    this.isLoadingBidUnit = true;
    this.showEditIcon = false;
    this.currentBidUnit = null;

    const filters = {
      group: group,
      slug: this.vehicleData.slug,
      company_slug: this.vehicleData.company_slug,
      paginate: "false",
      bid_date: this.vehicleData.auction_date_only
    };

    this.subscriptions.push(
      this._bidUnitService.GetData('1', '', filters).subscribe({
        next: (response: any) => {
          this.isLoadingBidUnit = false;

          if (response.status && response.data && response.data.length > 0) {
            // Found bid unit data - populate the field and show edit icon
            const bidUnitData = response.data[0];
            this.currentBidUnit = bidUnitData;
            this.bidForm.patchValue({ numberOfUnits: bidUnitData.number_of_units || '' });
            this.showEditIcon = true;
            this.bidForm.get('numberOfUnits')?.disable();
          } else {
            // No data found - show empty field and enable input
            this.bidForm.get('numberOfUnits')?.enable();
            this.bidForm.patchValue({ numberOfUnits: '' });
            this.showEditIcon = false;
            this.currentBidUnit = null;
          }
        },
        error: (error: any) => {
          this.isLoadingBidUnit = false;
          console.error('Error fetching bid unit data:', error);

          // Enable the field with empty value on error
          this.bidForm.patchValue({ numberOfUnits: '' });
          this.bidForm.get('numberOfUnits')?.enable();
          this.showEditIcon = false;
          this.currentBidUnit = null;
        }
      })
    );
  }

  private getVehicleIdFromRoute(): void {
    this.vehicleId = this.route.snapshot.paramMap.get('car_id') || '';
    if (!this.vehicleId) {
      console.error('No vehicle ID found in route');
      this.router.navigate(['/bid-manager/search-bids']);
    }
  }

  private loadVehicleData(): void {
    if (!this.vehicleId) return;

    this.isLoading = true;
    this.subscriptions.push(
      this._carService.GetDataById(this.vehicleId).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.vehicleData = response.data;
            if (this.vehicleData && this.vehicleData.pictures && this.vehicleData.type != 'aucnet')
              this.vehicleData.pictures =
                this.vehicleData?.pictures?.length
                  ? [...this.vehicleData.pictures.slice(1), this.vehicleData.pictures[0]]
                  : [];
            const hasAccess = this.checkUserHasAccess(this.vehicleData?this.vehicleData.type: null);
            console.log('Has access:', hasAccess);
            this.setupDefaultBidAmount();
          } else {
            console.error('Failed to load vehicle data');
          }
          this.isLoading = false;
        },
        error: (error: any) => {
          console.error('Error loading vehicle data:', error);
          this.isLoading = false;
        }
      })
    );
  }

  private setupDefaultBidAmount(): void {
    if (this.vehicleData?.price) {
      const currentPrice = parseInt(this.vehicleData.price);
      this.bidForm.patchValue({
        bidAmount: currentPrice,
        maxBid: Math.round(currentPrice * 1.1) // 10% higher as default max bid
      });
    }
  }

  // Image gallery methods
  selectImage(index: number): void {
    this.selectedImageIndex = index;
  }

  nextImage(): void {
    if (this.vehicleData?.pictures && this.selectedImageIndex < this.vehicleData.pictures.length - 1) {
      this.selectedImageIndex++;
    }
  }

  previousImage(): void {
    if (this.selectedImageIndex > 0) {
      this.selectedImageIndex--;
    }
  }

  // Tab navigation
  setActiveTab(tab: string): void {
    this.activeTab = tab;
  }

  // Inspection sheet toggle
  toggleInspectionSheet(): void {
    this.showInspectionSheet = !this.showInspectionSheet;
  }

  // Bid submission
  onSubmitBid(): void {
    if (this.bidForm.valid && !this.isSubmittingBid) {
      this.isSubmittingBid = true;

      // Use getRawValue() to get all values including disabled fields
      const formValues = this.bidForm.getRawValue();

      const bidData = {
        vehicle_id: this.vehicleId,
        bid_amount: formValues.bidAmount,
        currency: formValues.currency,
        bid_type: formValues.bidType,
        number_of_units: formValues.numberOfUnits || null,
        group: formValues.group,
        message: formValues.message || '',
        price_type: formValues.priceType,
        price_type_amount: formValues.priceTypeAmount,
        client_id: formValues.client?._id || null,
        destination_country: formValues.destinationCountry || null,
        destination_port: formValues.destinationPort || null
      };

      console.log('Submitting bid:', bidData);

      // Submit bid using VehicleBidsService
      this.subscriptions.push(
        this._vehicleBidsService.AddData(bidData).subscribe({
          next: (response: any) => {
            this.isSubmittingBid = false;
            console.log('Bid submission response:', response);

            if (response.status) {
              // Success message
              this.showSuccessMessage('Bid submitted successfully!');
              // Reset form
              this.resetBidForm();
            } else {
              // Handle API error response
              this.showErrorMessage(response.message || 'Failed to submit bid. Please try again.');
            }
          },
          error: (error: any) => {
            this.isSubmittingBid = false;
            console.error('Error submitting bid:', error);

            // Show user-friendly error message
            let errorMessage = 'An error occurred while submitting your bid. Please try again.';
            if (error.message && error.error.message) {
              errorMessage = error.error.message;
            } else if (error.message) {
              errorMessage = error.message;
            }

            this.showErrorMessage(errorMessage);
          }
        })
      );
    } else {
      this.markFormGroupTouched();
    }
  }

  // Inquiry submission
  onSubmitInquiry(): void {
    if (this.inquiryForm.valid && !this.isSubmittingInquiry) {
      this.isSubmittingInquiry = true;

      const inquiryData = {
        vehicle_id: this.vehicleId,
        message: this.inquiryForm.value.inquiryMessage
      };

      console.log('Submitting inquiry:', inquiryData);

      // Submit inquiry using BidInquiryService
      this.subscriptions.push(
        this._bidInquiryService.AddData(inquiryData).subscribe({
          next: (response: any) => {
            this.isSubmittingInquiry = false;
            console.log('Inquiry submission response:', response);

            if (response.status) {
              // Success message
              this.showSuccessMessage('Inquiry sent successfully!');
              // Reset form
              this.resetInquiryForm();
            } else {
              // Handle API error response
              this.showErrorMessage(response.message || 'Failed to send inquiry. Please try again.');
            }
          },
          error: (error: any) => {
            this.isSubmittingInquiry = false;
            console.error('Error submitting inquiry:', error);

            // Show user-friendly error message
            let errorMessage = 'An error occurred while sending your inquiry. Please try again.';
            if (error.error && error.error.message) {
              errorMessage = error.error.message;
            } else if (error.message) {
              errorMessage = error.message;
            }

            this.showErrorMessage(errorMessage);
          }
        })
      );
    } else {
      this.markInquiryFormTouched();
    }
  }

  // Show C&F Price functionality
  showCFPrice(): void {
    console.log('Show C&F Price clicked');
    // TODO: Implement C&F price calculation and display
    // This will be implemented when you provide the actual values
    alert('C&F Price calculation will be implemented with actual values');
  }

  // Open update bid unit modal
  openUpdateBidUnitModal(): void {
    if (this.currentBidUnit && this.updateBidUnitModal) {
      this.updateBidUnitModal.openModal(this.currentBidUnit);
    }
  }

  // Handle bid unit update callback
  onBidUnitUpdated(): void {
    console.log('Bid unit updated, refetching data...');
    // Refetch the number of units for the current group
    const currentGroup = this.bidForm.get('group')?.value;
    if (currentGroup) {
      this.onGroupChange(currentGroup);
    }
  }

  // Success message display
  private showSuccessMessage(message: string): void {
    Swal.fire({
      title: 'Success!',
      text: message,
      icon: 'success',
      confirmButtonText: 'OK',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'btn fw-bold btn-primary'
      }
    });
  }

  // Error message display
  private showErrorMessage(message: string): void {
    Swal.fire({
      title: 'Error!',
      text: message,
      icon: 'error',
      confirmButtonText: 'OK',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'btn fw-bold btn-primary'
      }
    });
  }

  // Reset bid form after successful submission
  private resetBidForm(): void {
    this.bidForm.reset({
      bidAmount: '',
      currency: 'JPY',
      bidType: 'Auction Price',
      grade: '',
      numberOfUnits: '',
      group: '',
      message: '',
      priceType: '',
      priceTypeAmount: '',
      client: null,
      destinationCountry: '',
      destinationPort: ''
    });
    // Disable numberOfUnits field after reset
    this.bidForm.get('numberOfUnits')?.disable();
    // Clear client search
    this.clearClientSearch();
    // Clear ports and C&F price
    this.ports = [];
    this.cfPrice = null;
    // Mark form as pristine and untouched
    this.bidForm.markAsPristine();
    this.bidForm.markAsUntouched();
  }

  private markFormGroupTouched(): void {
    Object.keys(this.bidForm.controls).forEach(key => {
      const control = this.bidForm.get(key);
      control?.markAsTouched();
    });
  }

  // Reset inquiry form after successful submission
  private resetInquiryForm(): void {
    this.inquiryForm.reset({
      inquiryMessage: ''
    });
    // Mark form as pristine and untouched
    this.inquiryForm.markAsPristine();
    this.inquiryForm.markAsUntouched();
  }

  private markInquiryFormTouched(): void {
    Object.keys(this.inquiryForm.controls).forEach(key => {
      const control = this.inquiryForm.get(key);
      control?.markAsTouched();
    });
  }

  // Client search methods
  private setupClientSearchDebounce(): void {
    this.clientSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchClients(searchTerm);
    });
  }

  onClientSearchInput(event: Event): void {
    // Don't allow search if the field is disabled (user role_type is 20)
    if (this.isClientFieldDisabled) {
      return;
    }

    const target = event.target as HTMLInputElement;
    this.clientSearchTerm = target.value;
    this.selectedClient = null;
    this.clientSearchSubject.next(this.clientSearchTerm);
    this.showClientDropdown = true;
  }

  onClientInputFocus(): void {
    // Don't show dropdown if the field is disabled (user role_type is 20)
    if (this.isClientFieldDisabled) {
      return;
    }

    if (this.clientSearchResults.length > 0) {
      this.showClientDropdown = true;
    }
  }

  onClientInputBlur(): void {
    setTimeout(() => {
      this.showClientDropdown = false;
    }, 200);
  }

  private searchClients(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.clientSearchResults = [];
      this.showClientDropdown = false;
      return;
    }

    this.isClientSearching = true;
    const filters = {
      role_type: '20',
      paginate: 'false'
    };

    this.subscriptions.push(
      this._userService.GetData('1', searchTerm, filters).subscribe({
        next: (response: any) => {
          this.isClientSearching = false;
          if (response.status && response.data) {
            // Handle both paginated and non-paginated responses
            this.clientSearchResults = Array.isArray(response.data) ? response.data : (response.data.data || []);
            this.showClientDropdown = this.clientSearchResults.length > 0;
          } else {
            this.clientSearchResults = [];
            this.showClientDropdown = false;
          }
        },
        error: (error: any) => {
          this.isClientSearching = false;
          console.error('Error searching clients:', error);
          this.clientSearchResults = [];
          this.showClientDropdown = false;
        }
      })
    );
  }

  selectClient(client: any): void {
    this.selectedClient = client;
    this.clientSearchTerm = this.getClientDisplayName(client);
    this.showClientDropdown = false;
    this.clientSearchResults = [];
    this.bidForm.patchValue({ client: client });

    // Auto-populate destination country and port if client has them
    if (client.country_obj && client.country_obj.code) {
      this.bidForm.patchValue({ destinationCountry: client.country_obj.code });
      // This will trigger the country change listener to load ports
    }
    if (client.port_obj && client.port_obj._id) {
      // Wait a bit for ports to load, then set the port
      setTimeout(() => {
        this.bidForm.patchValue({ destinationPort: client.port_obj._id });
      }, 500);
    }
  }

  clearClientSearch(): void {
    // Don't clear if the field is disabled (user role_type is 20)
    if (this.isClientFieldDisabled) {
      return;
    }

    this.clientSearchTerm = '';
    this.selectedClient = null;
    this.showClientDropdown = false;
    this.clientSearchResults = [];
    this.bidForm.patchValue({ client: null });
  }

  getClientDisplayName(client: any): string {
    if (client.name && client.company) {
      return `${client.name}, ${client.company}`;
    }
    return `${client.name || ''} ${client.last_name || ''}`.trim() || client.email || 'Unknown Client';
  }

  // Add to Real Stock functionality
  addToRealStock(): void {
    if (!this.vehicleData) return;

    // Show confirmation dialog
    Swal.fire({
      title: 'Add to Real Stock',
      text: `Are you sure you want to add "${this.vehicleData.name || 'this vehicle'}" to real stock?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, add it!',
      cancelButtonText: 'Cancel',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'btn btn-primary',
        cancelButton: 'btn btn-light'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        this.performAddToStock();
      }
    });
  }

  private performAddToStock(): void {
    if (!this.vehicleData) return;

    const vehicleId = this.vehicleData._id;
    this.isAddingToStock = true;

    this.subscriptions.push(
      this._vehicleService.AddToStock(vehicleId).subscribe({
        next: (response: any) => {
          this.isAddingToStock = false;

          if (response.status) {
            // Show success message
            Swal.fire({
              title: 'Success!',
              text: 'Vehicle has been successfully added to real stock.',
              icon: 'success',
              confirmButtonText: 'OK',
              buttonsStyling: false,
              customClass: {
                confirmButton: 'btn btn-primary'
              }
            });

            // Reload vehicle data to reflect changes
            this.loadVehicleData();
          } else {
            // Show error message
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to add vehicle to real stock.',
              icon: 'error',
              confirmButtonText: 'OK',
              buttonsStyling: false,
              customClass: {
                confirmButton: 'btn btn-primary'
              }
            });
          }
        },
        error: (error) => {
          this.isAddingToStock = false;
          console.error('Error adding vehicle to stock:', error);

          // Show error message
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while adding vehicle to real stock.',
            icon: 'error',
            confirmButtonText: 'OK',
            buttonsStyling: false,
            customClass: {
              confirmButton: 'btn btn-primary'
            }
          });
        }
      })
    );
  }

  // Check if vehicle type is aucnet or one-price
  isAucnetOrOnePrice(): boolean {
    return this.vehicleData?.type === 'aucnet' || this.vehicleData?.type === 'one-price';
  }

  // Check if vehicle type is alaedo
  isAlaedo(): boolean {
    return this.vehicleData?.type === 'alaedo';
  }

  // Utility methods
  formatPrice(price: string | number): string {
    const numPrice = typeof price === 'string' ? parseInt(price) : price;
    return new Intl.NumberFormat('en-US').format(numPrice);
  }

  formatAuctionDateTime(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatAuctionDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      // hour: '2-digit',
      // minute: '2-digit'
    });
  }

  formatAuctionTime(dateString: string): string {
    const date = new Date(dateString);
    let time = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
    return time;
  }

  getMainImage(): string {
    if (this.vehicleData?.pictures && this.vehicleData.pictures.length > 0) {
      return this.vehicleData.pictures[this.selectedImageIndex] || this.vehicleData.pictures[0];
    }
    return 'assets/media/stock/600x400/img-1.jpg';
  }

  getFeaturesList(): string[] {
    if (!this.vehicleData?.parsed_data?.Options) return [];
    return this.vehicleData.parsed_data.Options.split(' ').filter(option => option.trim());
  }

  // Navigation
  goBack(): void {
    this.router.navigate(['/bid-manager/search-bids']);
  }

  // Form validation helpers
  isFieldInvalid(fieldName: string): boolean {
    const field = this.bidForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.bidForm.get(fieldName);
    if (field?.errors) {
      if (field.errors['required']) {
        switch (fieldName) {
          case 'bidAmount': return 'Bid amount is required';
          case 'currency': return 'Currency is required';
          case 'bidType': return 'Auction price is required';
          case 'group': return 'Group selection is required';
          case 'priceType': return 'Price type is required';
          case 'priceTypeAmount': return 'Price type amount is required';
          default: return `${fieldName} is required`;
        }
      }
      if (field.errors['min']) {
        if (fieldName === 'bidAmount') return 'Minimum bid amount should be 6,000 JPY';
        if (fieldName === 'priceTypeAmount') return 'Price type amount must be greater than or equal to 0';
        return `${fieldName} must be greater than the minimum value`;
      }
    }
    return '';
  }

  // Inquiry form validation helpers
  isInquiryFieldInvalid(fieldName: string): boolean {
    const field = this.inquiryForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getInquiryFieldError(fieldName: string): string {
    const field = this.inquiryForm.get(fieldName);
    if (field?.errors) {
      if (field.errors['required']) {
        if (fieldName === 'inquiryMessage') return 'Message is required';
        return `${fieldName} is required`;
      }
    }
    return '';
  }

  // Load countries
  private loadCountries(): void {
    this.subscriptions.push(
      this._globalStateService.GetCountries().subscribe({
        next: (countries: any) => {
          this.countries = countries || [];
        },
        error: (error: any) => {
          console.error('Error loading countries:', error);
        }
      })
    );
  }

  // Handle destination country change
  onDestinationCountryChange(countryCode: string): void {
    if (countryCode) {
      this.loadPorts(countryCode);
    } else {
      this.ports = [];
      this.bidForm.patchValue({ destinationPort: '' });
    }
  }

  // Load ports based on country code
  private loadPorts(countryCode: string): void {
    if (!countryCode) {
      this.ports = [];
      return;
    }

    this.isLoadingPorts = true;
    this.subscriptions.push(
      this._portService.GetData('1', '', { paginate: false, country_code: countryCode }).subscribe({
        next: (response: any) => {
          this.isLoadingPorts = false;
          if (response.status && response.data) {
            this.ports = response.data.data || response.data || [];
          } else {
            this.ports = [];
          }
        },
        error: (error: any) => {
          this.isLoadingPorts = false;
          console.error('Error loading ports:', error);
          this.ports = [];
        }
      })
    );
  }

  // Handle destination port change
  onDestinationPortChange(portId: string): void {
    if (portId) {
      this.loadCFPrice(portId);
    } else {
      this.cfPrice = null;
    }
  }

  // Handle price type change
  onPriceTypeChange(priceType: string): void {
    const portId = this.bidForm.get('destinationPort')?.value;
    if (priceType === 'C&F' && portId) {
      this.loadCFPrice(portId);
    } else {
      this.cfPrice = null;
    }
  }

  // Load C&F Price
  private loadCFPrice(portId: string): void {
    const priceType = this.bidForm.get('priceType')?.value;

    // Only load C&F price if price type is C&F
    if (priceType !== 'C&F') {
      this.cfPrice = null;
      return;
    }

    if (!portId || !this.vehicleId) {
      this.cfPrice = null;
      return;
    }

    this.isLoadingCFPrice = true;
    const requestBody = {
      to_port_id: portId
    };

    this.subscriptions.push(
      this._carService.CarFreight(this.vehicleId, requestBody).subscribe({
        next: (response: any) => {
          this.isLoadingCFPrice = false;
          if (response.status && response.data && response.data.freight_amount !== undefined) {
            this.cfPrice = response.data.freight_amount;
          } else {
            this.cfPrice = null;
          }
        },
        error: (error: any) => {
          this.isLoadingCFPrice = false;
          console.error('Error loading C&F price:', error);
          this.cfPrice = null;
        }
      })
    );
  }
}
